import {Component, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {DateRangeSelectorComponent} from '../../../layout/date-range-selector/date-range-selector.component';
import {LoaderService} from '../../../../services/loader/loader.service';
import {ProductService} from '../../../../services/product/product.service';
import {PartnerService} from '../../../../services/partner/partner.service';
import {AccountingReportService} from '../../../../services/accounting/accounting-report.service';
import {Product} from '../../../../models/product';
import {Partner} from '../../../../models/partner/partner';
import {forkJoin} from 'rxjs';
import {NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MoneyPipe} from '../../../../pipes/money/money.pipe';
import {MetaTitle} from '../../../../decorator/meta-title';
import {IsGranted} from '../../../../decorator/is-granted';
import {AdvertiserService} from '../../../../services/advertiser/advertiser.service';
import {Advertiser} from '../../../../models/advertiser';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import {DateRange} from '../../../../types/date-range';
import {ListFilters} from '../../../../tools/list-filters/list-filters';
import {ListFilterService} from '../../../../services/list-filter/list-filter.service';
import {FileDownloadService} from '../../../../services/file-download/file-download.service';
import {UserActivityService} from '../../../../services/user-activity/user-activity.service';

@Component({
  selector: 'app-affiliate-report',
  imports: [
    DateRangeSelectorComponent,
    NgClass,
    FormsModule,
    MoneyPipe,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
  ],
  templateUrl: './affiliate-report.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './affiliate-report.component.scss'
})
@IsGranted(['AFFILIATE_PAYMENT_VIEW'])
@MetaTitle('Affiliate report')
export class AffiliateReportComponent implements OnInit {
  products: Product[] = [];
  filteredProducts: Product[] = [];
  affiliates: Partner[] = [];
  advertisers: Advertiser[] = [];
  rows: any[] = [];
  totals: any = {};
  loading = true;

  collapsed: { [key: string]: boolean } = {};

  sortColumn: string = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';

  dateRange!: DateRange;

  filters: ListFilters;

  @ViewChild(DateRangeSelectorComponent) dateSelector!: DateRangeSelectorComponent;

  constructor(
    private fileDownload: FileDownloadService,
    private userActivity: UserActivityService,
    private productService: ProductService,
    private partnerService: PartnerService,
    private advertiserService: AdvertiserService,
    private reportService: AccountingReportService,
    protected loader: LoaderService,
    private listFilterService: ListFilterService,
  ) {
    this.filters = listFilterService.createFilters({
      advertisers: 'number[]',
      affiliates: 'number[]',
      products: 'number[]',
    }, null, null, false);
  }

  ngOnInit(): void {
    this.loader.startLoading();

    forkJoin([
      this.advertiserService.findAll({sort: 'companyName'}),
      this.partnerService.findAll({sort: 'companyName', accountType: 'affiliate'}),
      this.productService.findAll({sort: 'name', groups: 'advertiser'}),
    ]).subscribe(([advertisers, affiliates, products]) => {
      this.advertisers = advertisers;
      this.affiliates = affiliates;
      this.products = products;
      this.filteredProducts = products;
      this.onAdvertiserChange();
      this.loader.stopLoading();
    });

    // The first fetch is triggered by the initial dateChanged emission of the date-range-selector
    this.filters.init(() => {
      if (this.dateRange) {
        this.filter();
      }
    });
  }

  dateChanged(dateRange: DateRange): void {
    this.dateRange = dateRange;
    this.filter();
  }

  applyFilters(): void {
    this.filters.apply();
  }

  filter(): void {
    this.loading = true;

    const params: any = {
      startDate: this.dateSelector ? this.dateSelector.getDateISO8601From() : this.dateRange.start.toISOString(),
      endDate: this.dateSelector ? this.dateSelector.getDateISO8601To() : this.dateRange.end.toISOString(),
      partnerAccountType: 'affiliate'
    };

    const filters = this.filters.toParams();

    if (filters['advertisers']) {
      params.advertiserId = filters['advertisers'].join(',');
    }

    if (filters['products']) {
      params.productId = filters['products'].join(',');
    }

    if (filters['affiliates']) {
      params.partnerId = filters['affiliates'].join(',');
    }

    this.loader.startLoading();
    this.userActivity.reportFilters(params);
    this.reportService.getAffiliateReport(params).subscribe((data: any) => {
      this.rows = data.rows;
      this.totals = data.totals;
      this.sortRows();
      this.loader.stopLoading();
      this.loading = false;
    });
  }

  onAdvertiserChange(): void {
    const advertisers: number[] = this.filters.values['advertisers'] ?? [];

    if (advertisers.length) {
      this.filteredProducts = this.products.filter(
        (product) => advertisers.includes(product.advertiser?.id as number)
      );
    } else {
      this.filteredProducts = this.products;
    }

    const filteredIds = this.filteredProducts.map((p) => p.id);
    this.filters.values['products'] = (this.filters.values['products'] ?? []).filter((id: number) => filteredIds.includes(id));
  }

  toggleAffiliate(affiliateId: number): void {
    const key = 'affiliate-' + affiliateId;
    this.collapsed[key] = !this.collapsed[key];
  }

  isAffiliateCollapsed(affiliateId: number): boolean {
    return !this.collapsed['affiliate-' + affiliateId];
  }

  toggleProduct(affiliateId: number, productId: number): void {
    const key = 'product-' + affiliateId + '-' + productId;
    this.collapsed[key] = !this.collapsed[key];
  }

  isProductCollapsed(affiliateId: number, productId: number): boolean {
    return !this.collapsed['product-' + affiliateId + '-' + productId];
  }

  sort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.sortRows();
  }

  private sortRows(): void {
    this.rows.sort((a: any, b: any) => {
      let valA: any;
      let valB: any;

      switch (this.sortColumn) {
        case 'name':
          valA = (a.name || '').toLowerCase();
          valB = (b.name || '').toLowerCase();
          break;
        case 'id':
          valA = a.id;
          valB = b.id;
          break;
        case 'volume':
          valA = a.totals.volume;
          valB = b.totals.volume;
          break;
        case 'spentUsd':
          valA = a.totals.spentUsd;
          valB = b.totals.spentUsd;
          break;
        case 'spentEur':
          valA = a.totals.spentEur;
          valB = b.totals.spentEur;
          break;
        case 'totalSpentUsd':
          valA = a.totals.totalSpentUsd;
          valB = b.totals.totalSpentUsd;
          break;
        case 'totalSpentEur':
          valA = a.totals.totalSpentEur;
          valB = b.totals.totalSpentEur;
          break;
        case 'averageCplUsd':
          valA = a.totals.averageCplUsd;
          valB = b.totals.averageCplUsd;
          break;
        case 'averageCplEur':
          valA = a.totals.averageCplEur;
          valB = b.totals.averageCplEur;
          break;
        default:
          return 0;
      }

      if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'bi-chevron-expand';
    return this.sortDirection === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
  }

  private csvAmount(value: any): string {
    const amount = parseFloat(value);
    return isNaN(amount) ? '' : (amount / 100).toFixed(2);
  }

  downloadCsv(): void {
    const headers = ['Affiliate', 'AffID', 'Volume of leads', 'Avg CPL (USD)', 'Avg CPL (EUR)', 'Spent (USD)', 'Spent (EUR)', 'Total spent (USD)', 'Total spent (EUR)'];
    const csvRows: string[] = [headers.join(',')];

    for (const affiliate of this.rows) {
      csvRows.push([
        `"${(affiliate.name || '').replace(/"/g, '""')}"`,
        affiliate.id,
        affiliate.totals.volume,
        this.csvAmount(affiliate.totals.averageCplUsd),
        this.csvAmount(affiliate.totals.averageCplEur),
        this.csvAmount(affiliate.totals.spentUsd),
        this.csvAmount(affiliate.totals.spentEur),
        this.csvAmount(affiliate.totals.totalSpentUsd),
        this.csvAmount(affiliate.totals.totalSpentEur),
      ].join(','));

      for (const product of affiliate.products || []) {
        csvRows.push([
          `"  ${(product.name || '').replace(/"/g, '""')}"`,
          '',
          product.totals.volume,
          this.csvAmount(product.totals.averageCplUsd),
          this.csvAmount(product.totals.averageCplEur),
          this.csvAmount(product.totals.spentUsd),
          this.csvAmount(product.totals.spentEur),
          this.csvAmount(product.totals.totalSpentUsd),
          this.csvAmount(product.totals.totalSpentEur),
        ].join(','));

        for (const lang of product.languages || []) {
          csvRows.push([
            `"    ${(lang.language || '-').replace(/"/g, '""')}"`,
            '',
            lang.volume,
            this.csvAmount(lang.averageCplUsd),
            this.csvAmount(lang.averageCplEur),
            this.csvAmount(lang.spentUsd),
            this.csvAmount(lang.spentEur),
            this.csvAmount(lang.totalSpentUsd),
            this.csvAmount(lang.totalSpentEur),
          ].join(','));
        }
      }
    }

    csvRows.push([
      '"GRAND TOTAL"',
      '',
      this.totals.volume,
      this.csvAmount(this.totals.averageCplUsd),
      this.csvAmount(this.totals.averageCplEur),
      this.csvAmount(this.totals.spentUsd),
      this.csvAmount(this.totals.spentEur),
      this.csvAmount(this.totals.totalSpentUsd),
      this.csvAmount(this.totals.totalSpentEur),
    ].join(','));

    this.fileDownload.saveCsv(csvRows.join('\n'), 'affiliate-report.csv', false);
  }
}
